import Cookies from "js-cookie";
import { createState, asDistinctDefinedSubscribable, } from '../helpers/state-subject';
import { cartState, onCartUpdate, updateCart } from './cart.service';
import { logInstanceId } from "../helpers/state";
logInstanceId("consent.service");
const PIEEYE_COOKIE_NAME = 'PieEyeConsent';
const PIEEYE_CART_CONSENT_ATTR = '_pieeye_cookie_consent';
let pendingPieeyeConsentCartSync = false;
const strictlyNecessaryState = createState(undefined);
const functionalState = createState(undefined);
const performanceState = createState(undefined);
const PIEEYE_CONSENT_MAP = {
    "pieeye-category-necessary": strictlyNecessaryState,
    "pieeye-category-functional": functionalState,
    "pieeye-category-performance": performanceState,
    "pieeye-category-preferences": undefined,
    "pieeye-category-targeting-analytics": undefined,
    "pieeye-category-unclassified": undefined
};
const DISPLAY_TO_CATEGORY_MAP = {
    "Strictly Necessary": "pieeye-category-necessary",
    "Functionality": "pieeye-category-functional",
    "Performance": "pieeye-category-performance",
    "Preferences": "pieeye-category-preferences",
    "Targeting/Advertising": "pieeye-category-targeting-analytics"
};
// these streams will fire each time the consent is updated
export const strictlyNecessaryCookieConsent = asDistinctDefinedSubscribable(strictlyNecessaryState);
export const functionalCookieConsent = asDistinctDefinedSubscribable(functionalState);
export const performanceCookieConsent = asDistinctDefinedSubscribable(performanceState);
function buildAllowedOnce(source, logMessage) {
    return {
        subscribe(listener) {
            let done = false;
            return source.subscribe((allowed) => {
                if (done || !allowed) {
                    return;
                }
                done = true;
                console.log(logMessage);
                listener();
            });
        },
    };
}
// these streams will fire only once when the cookie in question is allowed
// used when setting up modules to prevent re-initializing scripts
export const strictlyNecessaryCookieConsentAllowedOnce = buildAllowedOnce(strictlyNecessaryCookieConsent, "strictly necessary consent allowed once");
export const functionalCookieConsentAllowedOnce = buildAllowedOnce(functionalCookieConsent, "functional consent allowed once");
export const performanceCookieConsentAllowedOnce = buildAllowedOnce(performanceCookieConsent, "performance consent allowed once");
/**
 * checks various places for pieeye consent data
 * one note is that sometimes only localstorage is populated, while window.PieEyeConsent is a empty object and the cookie is missing
 * pieeye mentioned that this shouldn't happen, but they were unable to reproduce the issue so we're handling it the best we can
 * @returns the pieeye consent object
 */
function getConsent() {
    let consent = {};
    if (!isEmptyObject(window.PieEyeConsent)) {
        consent = window.PieEyeConsent;
    }
    else if (Cookies.get(PIEEYE_COOKIE_NAME)) {
        consent = JSON.parse(decodeURIComponent(Cookies.get(PIEEYE_COOKIE_NAME)));
    }
    else {
        // localstorage contains display names rather than category keys for some reason
        const storedConsent = window.localStorage.getItem('pieeye-consent') || window.localStorage.getItem('pieeye-categories-consent');
        if (storedConsent) {
            consent = storedConsent.split(",").map(s => s.trim()).reduce((acc, curr) => {
                const categoryKey = DISPLAY_TO_CATEGORY_MAP[curr];
                if (categoryKey) {
                    acc[categoryKey] = 1;
                }
                return acc;
            }, {});
        }
    }
    return consent;
}
export function allowsFunctionalSync() {
    const functionalConsent = getConsent()?.["pieeye-category-functional"] === 1;
    console.log('consents to functional', functionalConsent);
    return functionalConsent;
}
export function allowsPerformanceSync() {
    const performanceConsent = getConsent()?.["pieeye-category-performance"] === 1;
    console.log('consents to performance', performanceConsent);
    return performanceConsent;
}
onConsentUpdated(); // init
// listening for cookie changes because the "pieEyeShopifyConsent" event seems like it only happens
// when the customer changes consent themselves; it does not capture when the consent is set programmatically
if (window.cookieStore) { // doesn't exist on older versions of safari and firefox
    window.cookieStore.addEventListener("change", (e) => {
        const pieEyeCookie = e.changed?.find(c => c.name === PIEEYE_COOKIE_NAME);
        if (pieEyeCookie) {
            console.log('pieeye cookie consent changed (cs)', pieEyeCookie.value);
            onConsentUpdated();
        }
    });
}
else {
    let cachedVal = Cookies.get(PIEEYE_COOKIE_NAME);
    setInterval(() => {
        const newVal = Cookies.get(PIEEYE_COOKIE_NAME);
        if (cachedVal !== newVal) {
            cachedVal = newVal;
            console.log('pieeye cookie consent changed (i)', newVal);
            onConsentUpdated();
        }
    }, 500);
}
function onConsentUpdated() {
    const consent = getConsent();
    Object.entries(consent).forEach(([key, value]) => {
        const state = PIEEYE_CONSENT_MAP[key];
        if (state) {
            state.set(value === 1);
        }
    });
    syncPieeyeConsentCartAttribute();
}
function syncPieeyeConsentCartAttribute() {
    const consentLabel = window.localStorage.getItem('pieeye-consent') || 'unknown';
    const cart = cartState.get();
    if (!cart) {
        if (!pendingPieeyeConsentCartSync) {
            pendingPieeyeConsentCartSync = true;
            const unsubscribe = onCartUpdate.subscribe(() => {
                unsubscribe();
                pendingPieeyeConsentCartSync = false;
                syncPieeyeConsentCartAttribute();
            });
        }
        return;
    }
    const existing = cart.attributes?.[PIEEYE_CART_CONSENT_ATTR];
    if (existing === consentLabel) {
        return;
    }
    updateCart({ attributes: { [PIEEYE_CART_CONSENT_ATTR]: consentLabel } });
}
function isEmptyObject(obj) {
    if (obj === null)
        return true;
    if (typeof obj !== "object")
        return true;
    return Object.keys(obj).length === 0;
}
